import type { IOrganizationRepository } from "@/application/ports/repositories";
import type {
  UserOrganization,
  OrganizationUser,
  OrganizationTemplate,
  UpdateOrganizationRequest,
  OrganizationResponse,
} from "@/domain/organization";
import type { OrganizationInvitation } from "@/domain/invitation";
import { deadEndpoint } from "../deadEndpoint";
import type { OrganizationRole } from "../generated/types.gen";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

export class OrganizationRepository implements IOrganizationRepository {
  async updateOrganization(data: UpdateOrganizationRequest): Promise<OrganizationResponse> {
    return unwrap(sdk.updateOrganization({ body: data }));
  }

  async getOrganization(signal?: AbortSignal): Promise<OrganizationResponse> {
    return unwrap(sdk.getCurrentOrganization({ signal }));
  }

  async createOrganization(_data: {
    name: string;
    slug: string;
    email: string;
    logo?: string;
  }): Promise<{ logo_signed_url?: string }> {
    // Dead endpoint: POST /organizations never existed in the backend.
    return deadEndpoint("POST", "/organizations");
  }

  async getUserOrganizations(signal?: AbortSignal): Promise<UserOrganization[]> {
    const response = await unwrap(sdk.getMyMemberships({ signal }));
    return response.items;
  }

  async switchOrganization(
    organizationUuid: string | number,
  ): Promise<{ access_token?: string; refresh_token?: string }> {
    return unwrap(
      sdk.switchOrganization({ body: { organization_uuid: String(organizationUuid) } }),
    );
  }

  async getOrganizationUsers(signal?: AbortSignal): Promise<OrganizationUser[]> {
    const response = await unwrap(sdk.getMembers({ signal }));
    return response.items;
  }

  async updateMemberRole(memberUuid: string, role: string): Promise<OrganizationUser> {
    // roles round-trip from backend membership data; the enum validates server-side
    return unwrap(
      sdk.updateMembership({
        path: { membership_uuid: memberUuid },
        body: { role: role as OrganizationRole },
      }),
    );
  }

  async updateMemberStatus(memberUuid: string, is_active: boolean): Promise<OrganizationUser> {
    return unwrap(
      sdk.updateMembership({ path: { membership_uuid: memberUuid }, body: { is_active } }),
    );
  }

  async removeMember(memberUuid: string): Promise<void> {
    await unwrap(sdk.removeMembership({ path: { membership_uuid: memberUuid } }));
  }

  async getOrganizationInvitations(signal?: AbortSignal): Promise<OrganizationInvitation[]> {
    // spec gap: backend types invitation status as plain str (see wishlist)
    const response = await unwrap(sdk.getInvitations({ signal }));
    return response.items as OrganizationInvitation[];
  }

  async resendInvitation(invitationUuid: string): Promise<void> {
    await unwrap(sdk.resendInvitation({ path: { invitation_uuid: invitationUuid } }));
  }

  async cancelInvitation(invitationUuid: string): Promise<void> {
    await unwrap(sdk.revokeInvitation({ path: { invitation_uuid: invitationUuid } }));
  }

  async getOrganizationTemplates(signal?: AbortSignal): Promise<OrganizationTemplate[]> {
    const response = await unwrap(sdk.listTemplates({ signal }));
    return response.items;
  }

  async updateOrganizationLogo(logoBase64: string): Promise<{ logo_signed_url?: string }> {
    const organization = await unwrap(sdk.updateOrganization({ body: { logo: logoBase64 } }));
    return { logo_signed_url: organization.logo_signed_url ?? undefined };
  }
}
